import { Money } from '@palaccount/money';

/**
 * The DISPLAY-ONLY mirror of the server's PART 18 math — the same formulas,
 * the same Money helper, the same rounding order as
 * apps/api/src/invoices/totals.ts:
 *
 *   F-1  line_net    = qty × price × (1 − line_discount ÷ 100), rounded 2
 *   F-5  subtotal    = Σ line_net (each part rounded FIRST — N-7)
 *   F-2  document discount, two-way: the typed box drives, the derived box
 *        alone is rounded (SAL-00 §5f)
 *   F-3  taxable     = subtotal − discount_amount (never the gross subtotal)
 *   F-4  tax, two-way, on the TAXABLE base
 *   F-7  grand_total = taxable + tax_amount
 *   F-6  qty_total   = Σ line_qty — WITH A REPORTED GAP: F-6 says the total
 *        "must be hidden" on a document whose lines are in different units
 *        (N-14), and the editors cannot yet tell — the editable row state
 *        carries no unit (the picker hands over name and price only), so the
 *        footer shows the sum regardless. Fixing it means threading the
 *        item's unit through five editors' row state; flagged for the owner
 *        in the rule-queue report rather than half-done here.
 *
 * The server's totals are AUTHORITATIVE: every save returns the stored block
 * and the UI adopts it wholesale. This mirror only keeps the summary live
 * between keystrokes — if the two ever disagree, the server is right and the
 * mirror has a bug.
 *
 * Nothing here is ever a JS number (DECISION.md §2.1). Half-typed input
 * ("1.") simply yields null for the figures it blocks, drawn as an em dash.
 */

/**
 * The SHIPPED default VAT rate — the registry default of the company's
 * `invoicing.default_vat_pct` setting; editors pass the tenant's own value
 * (useOrgSetting), this constant is only the loading fallback.
 */
export const DEFAULT_TAX_PCT = '15';

export interface MirrorLineInput {
  qty: string;
  unitPrice: string;
  discountPct: string;
}

/** F-2/F-4 — which box was typed last, and exactly what it holds. */
export interface TwoWayState {
  basis: 'pct' | 'amount';
  value: string;
}

export interface MirrorLineView {
  /** F-1, or null while the row is incomplete/invalid. */
  lineTotal: string | null;
  /** The grey money under the Disc. box (SAL-00 §5d L7), or null. */
  discountAmount: string | null;
}

export interface TotalsView {
  lines: MirrorLineView[];
  /** The grid footer's QTY figure, trailing zeros trimmed. */
  qtyTotal: string;
  subtotal: string;
  discountPct: string | null;
  discountAmount: string | null;
  taxPct: string | null;
  taxAmount: string | null;
  grandTotal: string | null;
}

const AMOUNT_RE = /^\d+(\.\d+)?$/;

function money(value: string, currency: string): Money | null {
  if (!AMOUNT_RE.test(value.trim())) return null;
  try {
    return Money.of(value.trim(), currency);
  } catch {
    return null;
  }
}

/** "86.000" → "86", "2.500" → "2.5". Quantities only — money keeps its 2dp. */
function trimQty(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

/** F-1 — identical to the server's lineNet, null when the row is unusable. */
export function mirrorLine(line: MirrorLineInput, currency: string): MirrorLineView {
  const price = money(line.unitPrice, currency);
  const qty = line.qty.trim();
  if (price === null || !AMOUNT_RE.test(qty)) return { lineTotal: null, discountAmount: null };
  const pct = line.discountPct.trim() === '' ? '0' : line.discountPct.trim();
  if (!AMOUNT_RE.test(pct)) return { lineTotal: null, discountAmount: null };
  const gross = price.times(qty);
  const discount = gross.times(pct).dividedBy('100');
  return {
    lineTotal: gross.minus(discount).rounded().toFixed2(),
    discountAmount: discount.rounded().toFixed2(),
  };
}

/** F-2/F-4 — the server's twoWay: only the DERIVED side is computed+rounded. */
function mirrorTwoWay(
  input: TwoWayState | undefined,
  base: Money,
  fallbackPct: string,
  currency: string,
): { pct: string; amount: string } | null {
  if (input !== undefined && input.basis === 'amount') {
    const amount = money(input.value, currency);
    if (amount === null) return null;
    const rounded = amount.rounded();
    const pct = base.isPositive()
      ? rounded.dividedBy(base.toDecimalString()).times('100').rounded()
      : Money.zero(currency);
    // The typed side is echoed exactly as typed by the CALLER; these are the
    // derived figures.
    return { pct: pct.toFixed2(), amount: rounded.toFixed2() };
  }
  const pctRaw = input !== undefined ? input.value : fallbackPct;
  const pct = money(pctRaw, currency);
  if (pct === null) return null;
  const amount = base.times(pctRaw.trim()).dividedBy('100').rounded();
  return { pct: pct.rounded().toFixed2(), amount: amount.toFixed2() };
}

export function computeTotalsMirror(
  lines: readonly MirrorLineInput[],
  discount: TwoWayState | undefined,
  tax: TwoWayState | undefined,
  currency = 'USD',
  defaultTaxPct: string = DEFAULT_TAX_PCT,
): TotalsView {
  const lineViews = lines.map((line) => mirrorLine(line, currency));

  // F-5 / N-7: rounded parts, then the sum. Unusable rows contribute nothing
  // (the server never sees them — the save prunes/refuses).
  let subtotal = Money.zero(currency);
  for (const view of lineViews) {
    if (view.lineTotal !== null) subtotal = subtotal.plus(Money.of(view.lineTotal, currency));
  }

  let qty = Money.zero(currency);
  for (const line of lines) {
    const value = line.qty.trim();
    if (AMOUNT_RE.test(value)) qty = qty.plus(Money.of(value, currency));
  }

  const documentDiscount = mirrorTwoWay(discount, subtotal, '0', currency);
  if (documentDiscount === null) {
    return {
      lines: lineViews,
      qtyTotal: trimQty(qty.toDecimalString()),
      subtotal: subtotal.toFixed2(),
      discountPct: null,
      discountAmount: null,
      taxPct: null,
      taxAmount: null,
      grandTotal: null,
    };
  }

  // F-3 — tax on the DISCOUNTED base.
  const taxable = subtotal.minus(Money.of(documentDiscount.amount, currency));
  const documentTax = mirrorTwoWay(tax, taxable, defaultTaxPct, currency);
  if (documentTax === null) {
    return {
      lines: lineViews,
      qtyTotal: trimQty(qty.toDecimalString()),
      subtotal: subtotal.toFixed2(),
      discountPct: documentDiscount.pct,
      discountAmount: documentDiscount.amount,
      taxPct: null,
      taxAmount: null,
      grandTotal: null,
    };
  }

  // F-7 — every part already rounded (N-7).
  const grandTotal = taxable.plus(Money.of(documentTax.amount, currency));

  return {
    lines: lineViews,
    qtyTotal: trimQty(qty.toDecimalString()),
    subtotal: subtotal.toFixed2(),
    discountPct: documentDiscount.pct,
    discountAmount: documentDiscount.amount,
    taxPct: documentTax.pct,
    taxAmount: documentTax.amount,
    grandTotal: grandTotal.toFixed2(),
  };
}

/** F-9's tolerance test, mirrored for the balance-due badge colour. */
export function isFullyPaid(balanceDue: string, currency = 'USD'): boolean {
  const balance = money(balanceDue, currency);
  return balance !== null && balance.isZero('0.0001');
}
